/*
 * 1-Minute Maths
 * Copyright © 2021 - 2024 White Rose Maths
 * QuestionStatement.web.tsx - Component to render the statement part of a question
 */

import { unreachable } from '../../../utils/typescript';
import GroupArray from '../GroupArray';
import './QuestionStatement.web.css';
import { Format, isAdditiveSequenceQuestion, isEqualGroupsQuestion, isEqualGroupsCountersQuestion, isEquationQuestion, isOddOrEvenQuestion, isWordEquationQuestion, Formation } from '../types';
import QuestionStatementProps from './QuestionStatementProps';

import type { JSX } from 'react';

const NO_BREAK_SPACE = '\u00A0';

const QuestionStatement = ({ question, userAnswer }: QuestionStatementProps): JSX.Element => {
  const ans = userAnswer !== '' ? userAnswer : NO_BREAK_SPACE + NO_BREAK_SPACE;

  if (question === undefined) {
    return (
      <div id="question_view">
        <p className="question_text">Loading Question</p>
      </div>
    );

  } else if (isEquationQuestion(question)) {
    // Equation question
    const numA = <p className="question_text" key="numA">{question.numA}</p>;
    const sign = <p className="question_text" key="sign">{question.sign}</p>;
    const numB = <p className="question_text" key="numB">{question.numB}</p>;
    const eq = <p className="question_text" key="eq">=</p>;
    const answer = <p className="question_text" id="question_answer" key="answer">{ans}</p>;
    const equation = (elements: JSX.Element[]) => <div id="question_view">{elements}</div>;

    // Order of array passed to `equation` is important
    switch (question.format) {
      case Format.Equation1:
        return equation([numA, sign, numB, eq, answer]);
      case Format.Equation2:
        return equation([answer, eq, numA, sign, numB]);
      case Format.Equation3:
        return equation([numB, sign, numA, eq, answer]);
      case Format.Equation4:
        return equation([answer, eq, numB, sign, numA]);
      case Format.Equation5:
        return equation([numA, sign, answer, eq, numB]);
      case Format.Equation6:
        return equation([answer, sign, numA, eq, numB]);
      case Format.Equation7:
        return equation([numB, eq, numA, sign, answer]);
      case Format.Equation8:
        return equation([numB, eq, answer, sign, numA]);
    }

  } else if (isWordEquationQuestion(question)) {
    // Word equation question
    return (
      <div id="question_view">
        <p className="question_text">{question.action}</p>
        <p className="question_text">{question.numA}</p>
        <p className="question_text">is</p>
        <p className="question_text" id="question_answer">{ans}</p>
      </div>
    );

  } else if (isAdditiveSequenceQuestion(question)) {
    // Sequence question
    const answer = <p className="question_text" id="question_answer" key="answer">{ans}</p>;
    const comma = <p className="question_text" key="comma">,  </p>;
    const num = (diff: number) => <p className="question_text" key={diff}>{question.answer + diff * question.sequence}, </p>;
    const endNum = (diff: number) => <p className="question_text" key={diff}>{question.answer + diff * question.sequence}</p>;
    const sequenceQuestion = (elements: JSX.Element[]) => <div id="question_view">{elements}</div>;

    switch (question.format) {
      case Format.AdditiveSequence1:
        return sequenceQuestion([answer, comma, num(1), num(2), num(3), endNum(4)]);
      case Format.AdditiveSequence2:
        return sequenceQuestion([num(-1), answer, comma, num(1), num(2), endNum(3)]);
      case Format.AdditiveSequence3:
        return sequenceQuestion([num(-2), num(-1), answer, comma, num(1), endNum(2)]);
      case Format.AdditiveSequence4:
        return sequenceQuestion([num(-3), num(-2), num(-1), answer, comma, endNum(1)]);
      case Format.AdditiveSequence5:
        return sequenceQuestion([num(-4), num(-3), num(-2), num(-1), answer]);
    }

  } else if (isOddOrEvenQuestion(question)) {
    // Odd or even question
    return (
      <div className="odd_even_question_view">
        <p className="question_text">{question.action}</p>
        <p className="odd_even_number_text">{question.numA}</p>
      </div>
    );

  } else if (isEqualGroupsQuestion(question)) {
    // Equal groups (images) question
    const equalGroups = (groups: ('a' | 'b')[]) => {
      let aCount = 0;
      let bCount = 0;
      const components = groups.map(group => group === 'a' ?
        <img src={question.imageA} className="equalGroupImages" key={'a' + aCount++}/> :
        <img src={question.imageB} className="equalGroupImages" key={'b' + bCount++}/>
      );

      return <div className="equalGroupsQuestionView">
        <p className="equalGroupsText">{question.action}</p>
        <div className="equalGroupsImageView">{ components }</div>
      </div>;
    };

    switch (question.format) {
      case Format.EqualGroupsAB:
        return equalGroups(['a', 'b']);
      case Format.EqualGroupsAAB:
        return equalGroups(['a', 'a', 'b']);
      case Format.EqualGroupsABA:
        return equalGroups(['a', 'b', 'b']);
      case Format.EqualGroupsBAA:
        return equalGroups(['b', 'a', 'a']);
      case Format.EqualGroupsBAAA:
        return equalGroups(['b', 'a', 'a', 'a']);
      case Format.EqualGroupsABAA:
        return equalGroups(['a', 'b', 'a', 'a']);
      case Format.EqualGroupsAABA:
        return equalGroups(['a', 'a', 'b', 'a']);
      case Format.EqualGroupsAAAB:
        return equalGroups(['a', 'a', 'a', 'b']);
      case Format.EqualGroupsABBA:
        return equalGroups(['a', 'b', 'b', 'a']);
      case Format.EqualGroupsABAB:
        return equalGroups(['a', 'b', 'a', 'b']);
    }

  } else if (isEqualGroupsCountersQuestion(question)) {
    // Equal groups (counters) question
    const equalGroupCounters = (groups: [number, Formation][]) => {
      const components = groups.map(([, formation], index) => <GroupArray formation={formation} key={index}/>);
      return <div className="equalGroupsQuestionView">
        <p className="equalGroupsCounterText">{question.action}</p>
        <div className="equalGroupsCounterView">{components}</div>
      </div>;
    };

    switch (question.format) {
      case Format.EqualGroupsCountersAB:
        return equalGroupCounters([
          [question.numA, question.formationA],
          [question.numB, question.formationB]
        ]);
      case Format.EqualGroupsCountersABC:
        return equalGroupCounters([
          [question.numA, question.formationA],
          [question.numB, question.formationB],
          [question.numC, question.formationC]
        ]);
      case Format.EqualGroupsCountersABCD:
        return equalGroupCounters([
          [question.numA, question.formationA],
          [question.numB, question.formationB],
          [question.numC, question.formationC],
          [question.numD, question.formationD]
        ]);
    }

  } else {
    unreachable(question);
  }
};

export default QuestionStatement;
